import API from "@/api/API.js";

/**
 * Single download primitive used for individual files AND the final zip.
 * Uses a detached iframe so the click is not swallowed by Three.js
 * transform controls (same workaround the old ExcelExport relied on).
 */
export function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const iframe = document.createElement("iframe");
  iframe.style.display = "none";
  document.body.appendChild(iframe);
  iframe.contentWindow.document.write(
    `<a id="download" download="${filename}" href="${url}"></a>`
  );
  iframe.contentWindow.document.getElementById("download").click();
  setTimeout(() => {
    document.body.removeChild(iframe);
    URL.revokeObjectURL(url);
  }, 1000);
}

/**
 * Fetches the annotations XLSX as a blob using the existing endpoint.
 * Returns the blob plus the companyname response header (for analytics).
 */
export async function fetchXlsxBlob(payload) {
  const response = await API.airteam3DViewer.getXlsx(payload);
  return {
    blob: new Blob([response.data]),
    companyName: response.headers["companyname"],
  };
}

/**
 * Re-fetches the GLB from the exact URL used at load time (so the browser
 * HTTP cache usually serves it). onProgress receives 0..1; if the response
 * has no Content-Length, onProgress is never called (caller shows indeterminate).
 */
export async function fetchModelBlob(url, { onProgress, signal } = {}) {
  const response = await fetch(url, { signal });
  if (!response.ok) {
    throw new Error(`Model fetch failed: ${response.status}`);
  }
  const total = Number(response.headers.get("content-length")) || 0;
  if (!response.body || !total) {
    // No readable stream or unknown length — can't report progress.
    return await response.blob();
  }
  const reader = response.body.getReader();
  const chunks = [];
  let received = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    received += value.length;
    if (onProgress) onProgress(received / total);
  }
  return new Blob(chunks);
}

/**
 * Builds an <img> tag with explicit width AND height attributes.
 * html2pdf measures page breaks before images finish loading; declared
 * dimensions reserve the correct box up front so nothing shifts mid-render
 * and gets cut across pages. Throws if the image can't be loaded.
 */
export async function sizedImgTag(src, width, style = "") {
  const probe = new Image();
  probe.src = src;
  await probe.decode();
  const height = Math.round((probe.naturalHeight / probe.naturalWidth) * width);
  return `<img src="${src}" width="${width}" height="${height}" style="${style}" />`;
}

/**
 * Renders report HTML to a real PDF blob via html2pdf.js (dynamically imported).
 */
export async function renderHtmlToPdfBlob(html, filename) {
  const { default: html2pdf } = await import("html2pdf.js");
  // Pass the HTML as a string so html2pdf creates, sizes and positions the
  // render container itself. A hand-rolled off-screen container (e.g. left:
  // -99999px) makes html2canvas capture an empty region -> blank PDF.
  return html2pdf()
    .set({
      filename,
      margin: 10,
      image: { type: "jpeg", quality: 0.95 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      pagebreak: { mode: ["css", "legacy"] },
    })
    .from(html, "string")
    .outputPdf("blob");
}
